import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import DiscountSection from "../../components/DiscountSection";
import {
  getAllEducationLevels,
  getEducationLevelSubclass,
} from "../../services/educationLevelServices";
import { getAllDiscounts } from "../../services/schoolDiscountsServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import graduateIcon from "../../assets/graduate.png";
import {
  addAcademicYear,
  graduateStudent,
} from "../../services/StudentServices";
import Dropdown from "../../components/Dropdown";
import HotdropdownV2 from "../../components/HotdropdownV2";
import CelebrationPopup from "./CelebrationPopup";
import AreYouSureModal from "../../components/AreYouSureModal";

const AddAcademicYear = ({
  isOpen,
  onClose,
  onAcademicYearAdded,
  studentId,
  studentInfo,
}) => {
  const { t } = useTranslation("general");

  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [educationLevels, setEducationLevels] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [discountType, setDiscountType] = useState("percentage");
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [graduateLoading, setGraduateLoading] = useState(false);
  const [subclasses, setSubclasses] = useState([]);

  const [academicYearFields, setAcademicYearFields] = useState({
    source: "continues",
    registerDate: new Date().toISOString().split("T")[0],
    studentClass: {
      title: "",
      value: "",
      price: 0,
    },
    subclass: {
      id: "",
      name: "",
    },
    discount: {
      title: "",
      value: "",
    },
    discountAmount: 0,
  });

  const fetchSubclasses = async (educationLevelId) => {
    try {
      const response = await getEducationLevelSubclass(educationLevelId);
      const formattedSubclasses = response.data.subClasses.map((subclass) => ({
        title: `${subclass.name} (${t("students")}: ${subclass.studentCount})`,
        value: subclass.id,
        name: subclass.name,
      }));
      setSubclasses(formattedSubclasses);
    } catch (error) {
      console.error("Error fetching subclasses:", error);
      setSubclasses([]);
    }
  };

  const fetchEducationLevels = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm,
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
      }));

      if (page === 1) {
        setEducationLevels(transformedData);
      } else {
        setEducationLevels((prev) => [...prev, ...transformedData]);
      }

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      setEducationLevels([]);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const fetchAllDiscounts = async () => {
    try {
      const response = await getAllDiscounts();

      if (response.status === 200) {
        const formatedDiscounts = response.data.discounts.map((discount) => ({
          title: discount.name,
          value: discount.id,
        }));

        setDiscounts([
          ...formatedDiscounts,
          { title: "other", value: "" },
          { title: "none", value: "" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching discounts:", error);
    }
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setAcademicYearFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleAddAcademicYear = async (e) => {
    e.preventDefault();

    try {
      // Empty fields validation
      if (
        !academicYearFields.source ||
        !academicYearFields.registerDate ||
        !academicYearFields.studentClass.value ||
        !academicYearFields.subclass.id
      ) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      const academicYearData = {
        studentId: studentId,
        source: academicYearFields.source,
        registerDate: academicYearFields.registerDate,
        studentClassId: academicYearFields.studentClass.value,
        studentSubClassId: academicYearFields.subclass.id || null,
        discountId: academicYearFields.discount.value || null,
        ...((!academicYearFields.discount.value ||
          academicYearFields.discount.title === "other") && {
          isPercentage: discountType === "percentage",
          discountAmount: parseFloat(academicYearFields.discountAmount) || 0,
        }),
      };

      setLoadingModal(true);

      const response = await addAcademicYear(academicYearData);

      if (response.status === 201) {
        setError(false);
        setErrorMessage("");
        // Reset form
        setAcademicYearFields({
          source: "continues",
          registerDate: new Date().toISOString().split("T")[0],
          studentClass: {
            title: "",
            value: "",
            price: 0,
          },
          subclass: {
            id: "",
            name: "",
          },
          discount: {
            title: "",
            value: "",
          },
          discountAmount: 0,
        });
        // Call parent callback to refresh data
        if (onAcademicYearAdded) {
          onAcademicYearAdded();
        }
        onClose();
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        setError(true);
        setErrorMessage(errorMessage);
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage("An unexpected error occurred");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleGraduateStudent = async () => {
    try {
      setLoadingModal(true);
      setGraduateLoading(true);
      const response = await graduateStudent(studentId);

      if (response.status === 200) {
        // Show celebration popup
        setShowCelebration(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
      setErrorMessage(
        t("errorGraduatingStudent") || "Error graduating student"
      );
    } finally {
      setLoadingModal(false);
      setGraduateLoading(false);
      setShowAreYouSure(false);
    }
  };

  const handleCancel = () => {
    setError(false);
    setErrorMessage("");

    // Reset form
    setAcademicYearFields({
      source: "",
      registerDate: new Date().toISOString().split("T")[0],
      studentClass: {
        title: "",
        value: "",
        price: 0,
      },
      subclass: {
        id: "",
        name: "",
      },
      discount: {
        title: "",
        value: "",
      },
      discountAmount: 0,
    });

    onClose();
  };

  const handleCloseCelebration = () => {
    setShowCelebration(false);
    // Optionally close the main modal or refresh data
    if (onAcademicYearAdded) {
      onAcademicYearAdded();
    }
    onClose();
  };

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEducationLevels();
      fetchAllDiscounts();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <Modal
        title={t("changeStudentLevel")}
        onClose={handleCancel}
        withDropdown={true}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="edit-user-actions">
              <CustomButton
                title={t("confirm")}
                onClick={handleAddAcademicYear}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
                icon={closeIcon}
                iconSize={14}
              />
              <CustomButton
                title={t("graduateStudent")}
                onClick={() => setShowAreYouSure(true)}
                variant="blank"
                icon={graduateIcon}
                iconSize={22}
                disabled={loadingModal}
              />
            </section>
          </>
        }
      >
        <form onSubmit={handleAddAcademicYear}>
          <div>
            {error && (
              <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
                <ErrorBox>{errorMessage}</ErrorBox>
              </div>
            )}
            <div className="edit-user-entity">
              <div className="o-row o-row--gap-size_300">
                <span className="u-width--min-width_100">
                  {t("studentName")}:{" "}
                </span>
                <span className="t-bold_bold">{studentInfo.fullName}</span>
              </div>
              <div className="o-row o-row--gap-size_300 u-margin--bottom_300">
                <span className="u-width--min-width_100">
                  {t("studentId")}:{" "}
                </span>
                <span className="t-bold_bold">{studentInfo.studentCode}</span>
              </div>

              <span>{t("source")}: </span>
              <Dropdown
                options={[
                  { title: "kg2", value: "kg2" },
                  { title: "newStudent", value: "newStudent" },
                  { title: "transition", value: "transition" },
                  { title: "continues", value: "continues" },
                ]}
                selected={{
                  title: academicYearFields.source,
                  value: academicYearFields.source,
                }}
                setSelected={(selected) => {
                  handleInputChange(
                    {
                      target: {
                        value: selected.value,
                      },
                    },
                    "source"
                  );
                }}
                thick
              />

              <span>{t("registrationDate")}: </span>
              <CustomInput
                name="registerDate"
                type="date"
                value={academicYearFields.registerDate}
                onChange={(e) => handleInputChange(e, "registerDate")}
                errorText={
                  error && !academicYearFields.registerDate
                    ? t("inputFieldCannotBeEmpty")
                    : ""
                }
              />

              <span>{t("academicLevel")}: </span>
              <HotdropdownV2
                initOptions={[...educationLevels]}
                setOptions={setEducationLevels}
                selected={academicYearFields.studentClass.title}
                setSelected={(selected) => {
                  setAcademicYearFields((prev) => ({
                    ...prev,
                    studentClass: {
                      title: selected.title,
                      value: selected.value,
                      price: selected.price,
                    },
                    subclass: {
                      id: "",
                      name: "",
                    },
                  }));

                  if (selected.value) {
                    fetchSubclasses(selected.value);
                  } else {
                    setSubclasses([]);
                  }
                }}
                getData={(page, limit, isDropDown, query) =>
                  fetchEducationLevels(page, limit, isDropDown, query)
                }
                optionsName="educationLevels"
                thick
              />

              {academicYearFields.studentClass.title && (
                <>
                  <span>{t("subclass")}: </span>
                  <Dropdown
                    options={subclasses}
                    selected={{
                      title: academicYearFields.subclass.name
                        ? subclasses.find(
                            (sub) =>
                              sub.value === academicYearFields.subclass.id
                          )?.title || ""
                        : "",
                      value: academicYearFields.subclass.id,
                    }}
                    setSelected={(selected) => {
                      setAcademicYearFields((prev) => ({
                        ...prev,
                        subclass: {
                          id: selected.value,
                          name: selected.name,
                        },
                      }));
                    }}
                    thick
                  />
                </>
              )}

              <span>{t("discount")}: </span>
              <HotdropdownV2
                initOptions={[...discounts]}
                setOptions={setDiscounts}
                selected={t(academicYearFields.discount?.title)}
                setSelected={(selected) => {
                  handleInputChange(
                    { target: { value: selected } },
                    "discount"
                  );
                }}
                getData={fetchAllDiscounts}
                optionsName="discounts"
                thick
              />

              {academicYearFields.discount.title === "other" &&
              academicYearFields.discount.value === "" ? (
                <DiscountSection
                  originalPrice={academicYearFields.studentClass.price}
                  onPriceChange={() => {}}
                  inputMaxWidth="u-max-width_500"
                  setParentDiscountType={setDiscountType}
                  setParentDiscountAmount={(value) =>
                    handleInputChange({ target: { value } }, "discountAmount")
                  }
                />
              ) : null}
            </div>
          </div>
        </form>
      </Modal>

      {showAreYouSure && (
        <AreYouSureModal
          customMessage={t("sureGraduateStudent")}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleGraduateStudent}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm={true}
          loading={graduateLoading}
          confirmButtonVariant={null}
          confirmIconType="whiteCheck"

        />
      )}
      <CelebrationPopup
        isVisible={showCelebration}
        onClose={handleCloseCelebration}
        studentInfo={studentInfo}
      />
    </>
  );
};

export default AddAcademicYear;
